import React, { memo, useState } from 'react';
import { useLanguage } from '~/Hooks';
import { FormControlLabel } from '~/UIKit';
import { RadioGroup, Radio } from '@mui/material';
import { makeStyles } from '~/UIKit/Style';
import { FONT_SIZE_SMALLISH, FONT_SIZE_LARGISH } from '~/Constants/UI.constants';
import { PRIMARY_BLUE } from '~/Constants/Colors.constants';
import { ConfirmationModal } from '~/Components';

interface IRefundConfirmationModalnModal {
  onClose: () => void;
  onConfirm: (cancelledWithRefund: boolean) => void;
  open?: boolean;
}

const useStyles = makeStyles(
  (theme) => ({
    header: {
      width: '100%',
      minHeight: 46,
      padding: `10px 20px 0px`,
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      zIndex: 1,
      '& .MuiTypography-root': {
        fontSize: FONT_SIZE_LARGISH,
      },
    },
    title: {
      width: '100%',
      fontWeight: 'bold',
    },
    closeIcon: {
      width: 29,
      height: 29,
      color: PRIMARY_BLUE,
      position: 'absolute',
      top: 8,
      right: 10,
    },
    itemLabel: {
      '& .MuiTypography-root': {
        fontSize: FONT_SIZE_SMALLISH,
      },
    },
    container: {
      [theme.breakpoints.up('sm')]: {
        maxWidth: 380,
        minWidth: 380,
      },
    },
    content: {
      padding: '15px 20px 0',
      marginLeft: 'auto',
      marginRight: 'auto',
      width: '100%',
      textAlign: 'justify',
      'p + p': {
        paddingTop: 15,
      },
    },
    buttons: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      margin: '10px 15px',
      '& > button': {
        textTransform: 'capitalize',
      },
    },
  }),
  { name: 'RefundConfirmationModal' },
);

export const RefundConfirmationModal = memo(
  ({ onClose, onConfirm, open = false }: IRefundConfirmationModalnModal) => {
    const { t } = useLanguage();
    const { classes } = useStyles();
    const [itemSelected, setectItem] = useState('refund');

    const refundModalContent = (
      <>
        <div
          className={classes.content}
          onClick={(ev) => {
            ev.stopPropagation();
          }}
          data-testid={'RefundConfirmationModal'}
        >
          <RadioGroup aria-label="radioGroup" name={'refund'} onChange={() => setectItem('refund')}>
            <FormControlLabel
              value={'refund'}
              control={<Radio />}
              label={`${t('app.booking.cancel.modal.refund')}`}
              className={classes.itemLabel}
              checked={itemSelected === 'refund'}
            />
          </RadioGroup>

          <RadioGroup
            aria-label="radioGroup"
            name={'refund'}
            onChange={() => setectItem('norefund')}
          >
            <FormControlLabel
              value={'No-Refund'}
              control={<Radio />}
              label={`${t('app.booking.cancel.modal.norefund')}`}
              className={classes.itemLabel}
              checked={itemSelected === 'norefund'}
            />
          </RadioGroup>
        </div>
      </>
    );

    return (
      <ConfirmationModal
        title={t('app.booking.cancel.modal.title')}
        cancel={{ action: onClose }}
        confirm={{ action: () => onConfirm(itemSelected === 'refund') }}
        open={open}
        destructive
      >
        {refundModalContent}
      </ConfirmationModal>
    );
  },
);
